import React, { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ArtformCheck from "./ArtformCheck";
import FieldNoteCard from "./FieldNoteCard";
import FieldNoteSearch from "./FieldNoteSearch";

const API_URL = `${process.env.REACT_APP_API_FOLDER}list_fieldnotes/`;
const API_TOKEN = process.env.REACT_APP_API_TOKEN;
const PAGE_SIZE = 15;

const fetcher = (url) =>
  fetch(url, { headers: { "X-API-TOKEN": API_TOKEN } }).then((response) => {
    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.status}`);
    }
    return response.json();
  });

function FieldNotesSidebarWrap() {
  const { data, error, isLoading } = useSWR(API_URL, fetcher);
  const allFieldNotes = Array.isArray(data) ? data : [];
  const apiError = data && !Array.isArray(data) && data.error ? data.error : null;

  const [searchTerm, setSearchTerm] = useState("");
  const [artformFilters, setArtformFilters] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const filtered = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return allFieldNotes.filter((fieldNote) => {
      const searchableText = `${fieldNote.title || ""} ${fieldNote.name || ""}`.toLowerCase();
      if (normalizedSearch && !searchableText.includes(normalizedSearch)) {
        return false;
      }

      if (artformFilters.size > 0) {
        const artforms = String(fieldNote.artforms || "")
          .split(",")
          .map((artform) => artform.trim().toLowerCase())
          .filter(Boolean);

        if (![...artformFilters].some((artform) => artforms.includes(artform))) {
          return false;
        }
      }

      return true;
    });
  }, [allFieldNotes, searchTerm, artformFilters]);

  useEffect(() => setPage(1), [searchTerm, artformFilters]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPage((currentPage) => currentPage + 1);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore]);

  if (isLoading) {
    return (
      <div className="package-sidebar-wrapper pt-110">
        <div className="container">
          <div className="row g-4">
            {Array(6)
              .fill(null)
              .map((_, index) => (
                <div key={index} className="col-lg-4 col-md-6 fadeffect">
                  <Skeleton height={350} />
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || apiError) {
    return (
      <div className="package-sidebar-wrapper pt-110">
        <div className="container">
          <p className="text-danger">Error: {apiError || error.message}</p>
        </div>
      </div>
    );
  }

  const sidebar = (
    <div className="package-sidebar">
      <FieldNoteSearch value={searchTerm} onChange={setSearchTerm} />
      <ArtformCheck value={artformFilters} onChange={setArtformFilters} />
    </div>
  );

  return (
    <div className="package-sidebar-wrapper pt-110">
      <div className="container">
        <div className="row">
          <div className="col-lg-3 mb-4">
            {isMobile ? (
              <>
                <button
                  className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                  onClick={() => setSidebarOpen((open) => !open)}
                  type="button"
                  aria-expanded={sidebarOpen}
                >
                  <span>{sidebarOpen ? "Hide filters" : "Search and filter"}</span>
                  <i className="bi bi-funnel" />
                </button>
                <div
                  className={`collapse-slide mt-3 ${sidebarOpen ? "open" : ""}`}
                  style={{
                    maxHeight: sidebarOpen ? "1000px" : "0",
                    overflow: "hidden",
                    transition: "max-height 0.3s ease",
                  }}
                >
                  {sidebar}
                </div>
              </>
            ) : (
              sidebar
            )}
          </div>

          <div className="col-lg-9">
            <header className="fieldnotes-catalogue-intro">
              <h1>Field Notes</h1>
              <p>
                <br />
                Field Notes is a growing collection of writing from the CAMP community - tutors, alumni,
                related artists and team members — about the ideas, methods and questions that shape creative practice.
                <br /><br />
              </p>
            </header>

            <div className="row g-4">
              {visible.map((fieldNote) => (
                <div className="col-lg-4 col-md-6 fadeffect" key={fieldNote.id}>
                  <FieldNoteCard fieldNote={fieldNote} />
                </div>
              ))}
            </div>

            <div ref={sentinelRef} />

            {hasMore && (
              <div className="text-center mt-4">
                <button
                  onClick={() => setPage((currentPage) => currentPage + 1)}
                  className="btn btn-primary"
                  type="button"
                >
                  Load more
                </button>
              </div>
            )}

            {!visible.length && <p>No Field Notes match your filters.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FieldNotesSidebarWrap;
